import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import { Image, Link, Logo } from '@utils/cook/types'
import UltraModuleBundlingContext from '@contexts/ultraModuleBundlingContext'
import { useShowVideoCombination } from '@hooks/useShowVideoCombination'
import { JSONWidgetType as AdRingierJSONWidgetType } from '@widgets/AdRingier'
import { JSONWidgetType as RelatedMatchesJSONWidgetType } from '@widgets/RelatedMatches'
import { JSONWidgetType as TeaserStandardHorizontalJSONWidgetType } from '@widgets/TeaserStandardHorizontal'
import { JSONWidgetType as TeaserStandardHybridJSONWidgetType } from '@widgets/TeaserStandardHybrid'
import { JSONWidgetType as TeaserToiJSONWidgetType } from '@widgets/TeaserToi'
import { JSONWidgetType as TeaserToiCommercialJSONWidgetType } from '@widgets/TeaserToiCommercial'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import Bundling from '@components/Bundling'
import JSONRenderer from '@components/JSONRenderer'
import SectionHeader from '@components/SectionHeader'
import MainFlowWrapper from '@components/UltraModule/MainFlowWrapper'
import MobileOnlySeparator from '@components/UltraModule/MobileOnlySeparator'
import RelatedMatchesMobileAndTabletOnly from '@components/UltraModule/RelatedMatchesMobileAndTabletOnly'
import Separator from '@components/UltraModule/Separator'
import SideAd from '@components/UltraModule/SideAd'
import SideFlowWrapper from '@components/UltraModule/SideFlowWrapper'
import SportsPartner from '@components/UltraModule/SportsPartner'
import UltraModuleWrapper from '@components/UltraModule/Wrapper'

import { BlickBitesWrapper } from './Video/BlickBites'
import { StyledSwiper } from './VideosCarousel'

const {
  OuterWrapper,
  MainItemWrapper,
  RelatedItemsWrapper,
  Header: BundlingHeader,
  RelatedItemsSidebarWrapper,
} = Bundling

const StyledVideoCombinationWrapper = styled.div<{ isHidden: boolean }>`
  ${({ isHidden }) => css`
    ${isHidden &&
    css`
      display: none;
    `};
  `}
`

const StyledVideoCarouselWrapper = styled.div`
  &:empty {
    display: none;
  }
  ${StyledSwiper} {
    & .swiper-items-container {
      grid-template-rows: repeat(2, minmax(0, 1fr));
    }
  }
`

const StyledBlickBitesWrapper = styled.div`
  &:empty {
    display: none;
  }
  > ${BlickBitesWrapper} {
    padding-top: 0;
  }
`

export interface UltraModuleRelatedItemsProps {
  relatedItems: TeaserStandardHorizontalJSONWidgetType[]
  relatedItemsTitle?: string
}

export interface UltraModuleNoRelatedItemsProps {
  relatedItems?: undefined
  relatedMatches?: RelatedMatchesJSONWidgetType
}

export type UltraModuleMaybeRelatedItemsProps =
  | UltraModuleNoRelatedItemsProps
  | UltraModuleRelatedItemsProps

export interface UltraModuleCommonProps {
  mainItem: TeaserToiJSONWidgetType | TeaserToiCommercialJSONWidgetType
  unrelatedItems: TeaserStandardHybridJSONWidgetType[]
  secondaryItem?: TeaserToiJSONWidgetType | TeaserToiCommercialJSONWidgetType
  brandStudioAd?: AdRingierJSONWidgetType
  componentId?: string
  accentColor: string
  title?: string
  link?: Link
  logo?: Logo
  sideAd: AdRingierJSONWidgetType
  inlineAd?: AdRingierJSONWidgetType
  ctaText?: string
  partner?: {
    name: string
    logo: Image<'free'>
    title: string
    url: string
  }
}

export type UltraModuleAPIProps = UltraModuleCommonProps &
  UltraModuleMaybeRelatedItemsProps

export interface SideFlowWrapperProps {
  hasRelatedItems: boolean
  numberOfNonAdItemsInSideFlow: number
}

const UltraModule: FunctionComponent<UltraModuleAPIProps> = (props) => {
  const {
    componentId,
    mainItem,
    secondaryItem,
    brandStudioAd,
    accentColor,
    title,
    link,
    inlineAd,
    sideAd,
    unrelatedItems,
    relatedItems,
    partner,
    logo,
    ctaText,
  } = props

  const secondaryItemOrBrandStudioAd =
    brandStudioAd?.slotCodeDesktop &&
    brandStudioAd?.slotCodeTablet &&
    brandStudioAd?.slotCodeMobile
      ? brandStudioAd
      : secondaryItem

  const queryClient = useQueryClient()

  const hasRelatedItems = !!relatedItems

  const relatedMatches = !hasRelatedItems ? props.relatedMatches : undefined

  const showPortalVideoCombination =
    useShowVideoCombination() && componentId === 'ultra.1'

  const hasBundling = [
    relatedMatches,
    hasRelatedItems && relatedItems.length > 0,
  ].some(Boolean)

  const showUnrelatedItemsInSidebar = !hasRelatedItems && !relatedMatches

  const hasSecondaryItemOrBrandStudioAd = !!secondaryItemOrBrandStudioAd

  const hasInlineAd = !!inlineAd

  const hasPartner = !!partner

  const numberOfNonAdItemsInSideFlow =
    (hasRelatedItems ? relatedItems.length : 0) +
    (hasRelatedItems && props.relatedItemsTitle ? 1 : 0) +
    (relatedMatches ? 1 : 0) +
    (showUnrelatedItemsInSidebar ? 3 : 0)

  const relatedItemsMobileAndTabletOnly = useMemo(
    () =>
      hasRelatedItems
        ? relatedItems.map((relatedItem) => ({
            ...relatedItem,
            kind: ['teaser-standard-hybrid'],
            render: 'horizontal',
          }))
        : [],
    [hasRelatedItems, relatedItems],
  )

  const hasMiddleToi = useMemo(
    () => unrelatedItems.some((item) => item.variant === 'middle-toi'),
    [unrelatedItems],
  )

  const Wrapper = hasBundling ? OuterWrapper : UltraModuleWrapper
  const bundlingWrapperProps = {
    accentColor,
    hasTitle: !!title,
    hasLinkInTitle: !!link && !!ctaText,
  }

  const onRefChange = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        queryClient.setQueryData(['video-combination-wrapper-created'], true)
        queryClient.invalidateQueries({
          queryKey: ['video-combination-wrapper-created'],
          exact: true,
        })
      } else {
        queryClient.setQueryData(['video-combination-wrapper-created'], false)
        queryClient.invalidateQueries({
          queryKey: ['video-combination-wrapper-created'],
          exact: true,
        })
      }
    },
    [queryClient],
  )

  return (
    <>
      <UltraModuleBundlingContext.Provider value={hasBundling}>
        <Wrapper {...(hasBundling ? bundlingWrapperProps : {})}>
          {title && !hasBundling ? (
            <SectionHeader
              accentColor={accentColor}
              title={title}
              link={link}
              logo={logo}
              ctaText={ctaText}
            />
          ) : (
            <BundlingHeader
              accentColor={accentColor}
              title={title}
              link={link}
              ctaText={ctaText}
            />
          )}
          <MainFlowWrapper hasPartner={hasPartner}>
            <MainItemWrapper
              accentColor={accentColor}
              hasSportsPartner={!!partner}
            >
              <JSONRenderer>{mainItem}</JSONRenderer>
            </MainItemWrapper>
            <SportsPartner
              partner={partner}
              hasBundling={hasBundling}
              accentColor={accentColor}
            />
            {!!relatedMatches && (
              <RelatedItemsWrapper
                accentColor={accentColor}
                hasSportsPartner={!!partner}
              >
                <RelatedMatchesMobileAndTabletOnly>
                  {relatedMatches}
                </RelatedMatchesMobileAndTabletOnly>
              </RelatedItemsWrapper>
            )}
            {hasRelatedItems && (
              <RelatedItemsWrapper
                accentColor={accentColor}
                hasSportsPartner={!!partner}
              >
                <JSONRenderer>{relatedItemsMobileAndTabletOnly}</JSONRenderer>
              </RelatedItemsWrapper>
            )}
            {hasInlineAd ? (
              <JSONRenderer>{inlineAd}</JSONRenderer>
            ) : hasRelatedItems ? (
              <MobileOnlySeparator />
            ) : null}
            {showUnrelatedItemsInSidebar && (
              <JSONRenderer>
                {[
                  { ...unrelatedItems[2], render: 'vertical' },
                  { ...unrelatedItems[3], render: 'vertical' },
                ]}
              </JSONRenderer>
            )}
            {!showUnrelatedItemsInSidebar && (
              <JSONRenderer>
                {[
                  { ...unrelatedItems[0], render: 'vertical' },
                  { ...unrelatedItems[1], render: 'vertical' },
                ]}
              </JSONRenderer>
            )}
            <JSONRenderer>
              {{ ...unrelatedItems[0], render: 'horizontal' }}
            </JSONRenderer>
            <MobileOnlySeparator shouldBeHidden={hasMiddleToi} />
            <JSONRenderer>
              {{ ...unrelatedItems[1], render: 'horizontal' }}
            </JSONRenderer>
            {unrelatedItems.length > 2 && (
              <>
                <MobileOnlySeparator shouldBeHidden={hasMiddleToi} />
                <JSONRenderer>
                  {{ ...unrelatedItems[2], render: 'horizontal' }}
                </JSONRenderer>
                <MobileOnlySeparator shouldBeHidden={hasMiddleToi} />
                <JSONRenderer>
                  {{ ...unrelatedItems[3], render: 'horizontal' }}
                </JSONRenderer>
              </>
            )}
            {hasSecondaryItemOrBrandStudioAd && (
              <JSONRenderer>{secondaryItemOrBrandStudioAd}</JSONRenderer>
            )}
            {!showUnrelatedItemsInSidebar && unrelatedItems.length > 2 && (
              <JSONRenderer>
                {[
                  { ...unrelatedItems[2], render: 'vertical' },
                  { ...unrelatedItems[3], render: 'vertical' },
                ]}
              </JSONRenderer>
            )}
          </MainFlowWrapper>
          <SideFlowWrapper
            hasRelatedItems={hasRelatedItems}
            numberOfNonAdItemsInSideFlow={numberOfNonAdItemsInSideFlow}
            shouldShowBundling={hasBundling}
          >
            {!hasBundling && hasRelatedItems && !!props.relatedItemsTitle && (
              <SectionHeader
                title={props.relatedItemsTitle}
                isSubheader={true}
                accentColor={accentColor}
              />
            )}
            {hasRelatedItems && (
              <RelatedItemsSidebarWrapper accentColor={accentColor}>
                <JSONRenderer>{relatedItems}</JSONRenderer>
              </RelatedItemsSidebarWrapper>
            )}
            {!!relatedMatches && (
              <RelatedItemsSidebarWrapper accentColor={accentColor}>
                <JSONRenderer>{relatedMatches}</JSONRenderer>
              </RelatedItemsSidebarWrapper>
            )}
            {showUnrelatedItemsInSidebar && (
              <>
                <JSONRenderer>
                  {{ ...unrelatedItems[0], render: 'vertical' }}
                </JSONRenderer>
                <Separator />
                <JSONRenderer>
                  {{ ...unrelatedItems[1], render: 'vertical' }}
                </JSONRenderer>
              </>
            )}
            <SideAd hasRelatedItems={hasRelatedItems}>
              <JSONRenderer>{sideAd}</JSONRenderer>
            </SideAd>
          </SideFlowWrapper>
        </Wrapper>
      </UltraModuleBundlingContext.Provider>
      {componentId === 'ultra.1' && (
        <StyledVideoCombinationWrapper
          ref={onRefChange}
          id="video-combination"
          isHidden={!showPortalVideoCombination}
        >
          <StyledVideoCarouselWrapper id="video-carousel-combined-wrapper"></StyledVideoCarouselWrapper>
          <StyledBlickBitesWrapper id="blick-bites-combined-wrapper"></StyledBlickBitesWrapper>
        </StyledVideoCombinationWrapper>
      )}
    </>
  )
}

const widget = {
  kind: ['ultra-module'],
  component: UltraModule,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
